import { type EnrichedAuditEvent } from '@eppendorf/vnls-audit-event-client';
import { FetchInfiniteQueryOptions, QueryClient } from '@tanstack/react-query';
import { ReactElement } from 'react';
import { LoaderFunctionArgs } from 'react-router-dom';

import { prepopulateInfiniteQueryCache } from '$shared/utils/react-query.utils';

import { AuditTrailUpgradeDialog } from '$components/audit-trail-upgrade-dialog/audit-trail-upgrade-dialog';
import { AuditEventsOverview } from '$features/audit-events/audit-events-overview';
import { createAuditEventsQuery } from '$features/audit-events/audit-events.api';
import {
  AUDIT_EVENT_CATEGORIES,
  AuditEventCategory,
} from '$features/audit-events/components/filters/category-filter';
import {
  getSubscriptionSync,
  isEssentialsPlan,
  useGetSubscription,
} from '$features/subscription/subscription-api';

export async function auditEventsLoader(
  queryClient: QueryClient,
  args: LoaderFunctionArgs,
): Promise<null> {
  const subscription = await getSubscriptionSync(queryClient);
  if (subscription && isEssentialsPlan(subscription)) {
    return Promise.resolve(null);
  }

  const url = new URL(args.request.url);
  const searchParams = Object.fromEntries(url.searchParams.entries());
  const from = searchParams.from ?? null;
  const to = searchParams.to ?? null;
  const searchTerm = searchParams.searchTerm ?? null;
  const categoryParam = searchParams.category ?? null;

  const isAuditEventCategory = (value: unknown): value is AuditEventCategory =>
    AUDIT_EVENT_CATEGORIES.includes(value as AuditEventCategory);

  const category = isAuditEventCategory(categoryParam) ? categoryParam : null;

  return prepopulateInfiniteQueryCache<EnrichedAuditEvent[]>(
    queryClient,
    createAuditEventsQuery(from, to, searchTerm, category) as FetchInfiniteQueryOptions<
      EnrichedAuditEvent[]
    >,
  );
}

export function AuditEventsPage(): ReactElement {
  const { data: subscription, isLoading } = useGetSubscription();
  const isEssentials = !isLoading && !!subscription && isEssentialsPlan(subscription);
  return (
    <>
      <AuditTrailUpgradeDialog isOpen={isEssentials} />
      {!isEssentials && !isLoading && <AuditEventsOverview />}
    </>
  );
}
